import React, { useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import {
  Alert,
  Box,
  Button,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material'
import { AdvancedSettings, useAdvancedSettings } from '../api/AdvancedSettingsProvider'
import { advancedGet, advancedPut } from '../api/client'
import { useAdvancedResource } from '../api/useAdvancedResource'
import DialogHeader from '../components/DialogHeader'
import FailureMessage from '../components/FailureMessage'
import SectionCard from '../components/SectionCard'
import { roleLabelFor } from '../dashboard/careTeam/roleCategories'
import { adminFailureReason, isRejected } from './adminFailureReason'

type SettingKey = keyof AdvancedSettings
type Change = { key: SettingKey; value: string[] | number; title: string; enabled?: boolean }
type Response = { settings: AdvancedSettings; roles: string[] }

const RoleSetting = ({
  title,
  help,
  roles,
  selected,
  onSave,
  disabled,
}: {
  title: string
  help: string
  roles: string[]
  selected: string[]
  onSave: (change: Change) => void
  disabled: boolean
}) => (
  <SectionCard title={title} help={help} collapsible>
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {roles.map((role) => {
        const label = roleLabelFor(role)
        return (
          <Box key={role} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body1">{label}</Typography>
            <Switch
              checked={!selected.includes(role)}
              disabled={disabled}
              inputProps={{ 'aria-label': `Internal chat for ${label}` }}
              onChange={(_event, enabled) =>
                onSave({
                  key: 'careTeamChatExcludedRoles',
                  value: selected.includes(role) ? selected.filter((item) => item !== role) : [...selected, role],
                  title: `Turn ${enabled ? 'on' : 'off'} internal chat for ${label}?`,
                  enabled,
                })
              }
            />
          </Box>
        )
      })}
    </Box>
  </SectionCard>
)

const ThresholdSetting = ({
  initial,
  onSave,
  disabled,
}: {
  initial: number
  onSave: (change: Change) => void
  disabled: boolean
}) => {
  const [value, setValue] = useState(String(initial))
  const days = Number(value)
  const valid = Number.isInteger(days) && days >= 1 && days <= 365
  return (
    <SectionCard
      title="Stalled track threshold"
      help="Choose how many days without a lesson completion mark a running track as stalled. Applies to all Advanced patients."
      collapsible
    >
      <TextField
        label="Days without activity"
        type="number"
        value={value}
        disabled={disabled}
        onChange={(event) => setValue(event.target.value)}
        inputProps={{ min: 1, max: 365, step: 1 }}
        error={!valid}
        helperText={!valid ? 'Enter a whole number from 1 to 365.' : undefined}
        size="small"
      />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={disabled || !valid || days === initial}
          onClick={() => onSave({ key: 'stalledAfterDays', value: days, title: 'Change stalled track threshold?' })}
        >
          Save changes
        </Button>
      </Box>
    </SectionCard>
  )
}

const AdvancedAdminSettings = () => {
  const resource = useAdvancedResource((signal) => advancedGet<Response>('/admin/settings', signal), [])
  const { updateSettings } = useAdvancedSettings()
  const [saved, setSaved] = useState<AdvancedSettings>()
  const [pending, setPending] = useState<Change | null>(null)
  const [saving, setSaving] = useState(false)
  const { addToast } = useToasts()
  const settings = saved ?? (resource.status === 'ready' ? resource.data.settings : undefined)
  const roles = resource.status === 'ready' ? resource.data.roles : []
  const dismiss = () => {
    if (!saving) setPending(null)
  }
  const confirm = async () => {
    if (!pending) return
    setSaving(true)
    try {
      const response = await advancedPut<{ settings: AdvancedSettings }>(`/admin/settings/${pending.key}`, {
        value: pending.value,
      })
      setSaved(response.settings)
      updateSettings(response.settings)
    } catch (failure) {
      const reason = adminFailureReason(failure)
      addToast(
        <FailureMessage
          failure={
            isRejected(failure)
              ? { summary: 'Failed to save the change.', reason: `${reason} Nothing was changed.` }
              : {
                  summary: 'Failed to confirm the change.',
                  reason: `${reason} Reload Advanced config to check the saved settings before trying again.`,
                }
          }
        />,
        { appearance: 'error' }
      )
    } finally {
      setSaving(false)
      setPending(null)
    }
  }

  return (
    <>
      <SectionCard title="Symptom survey permissions" collapsible>
        <Typography>
          Restricting this feature to specific roles is not yet available to not break existing workflows.
        </Typography>
      </SectionCard>
      {resource.status === 'loading' && <LinearProgress />}
      {resource.status === 'error' && (
        <Alert severity="error" action={<Button onClick={resource.retry}>Retry</Button>}>
          <FailureMessage
            failure={{ summary: 'Failed to load Advanced settings.', reason: adminFailureReason(resource.error) }}
          />
        </Alert>
      )}
      {settings && (
        <>
          <RoleSetting
            title="Internal care team chat access"
            roles={roles}
            selected={settings.careTeamChatExcludedRoles}
            disabled={saving}
            onSave={setPending}
            help="Choose which active care team roles can use the internal group and direct chats (i.e., the care team chat)."
          />
          <ThresholdSetting initial={settings.stalledAfterDays} disabled={saving} onSave={setPending} />
        </>
      )}
      <Dialog open={pending !== null} onClose={dismiss} maxWidth="xs" fullWidth>
        <DialogHeader title={pending?.title ?? ''} onClose={dismiss} />
        <DialogContent>
          <Typography>This change applies to all Advanced patients.</Typography>
          {pending?.key === 'stalledAfterDays' && (
            <Typography sx={{ mt: 2 }}>Mark tracks stalled after {pending.value} days without activity.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={dismiss} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={confirm}
            variant="contained"
            disabled={saving}
            color={pending?.enabled === false ? 'error' : 'primary'}
          >
            {pending?.key === 'stalledAfterDays' ? 'Save changes' : `Turn ${pending?.enabled ? 'on' : 'off'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AdvancedAdminSettings
